import { AxiosRequestHeaders } from 'axios';
import { capability } from '../rpc-client/config/capabilities';
import RpcClientFactory from '../rpc-client/factory/rpc-client-factory';
import { DtoTypes } from '..';
import { ProductName_Top } from '../types/dto/pc/typekey';

export async function getOrCreateJob(
    accountNumber?: string,
    jobNumber?: string,
    policyNumber?: string,
    coverableId?: string,
    opcProduct?: DtoTypes.PC.typekey.ProductName_Top,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.JobInfoDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.JobInfoDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getOrCreateJob',
        [accountNumber, jobNumber, policyNumber, coverableId, opcProduct],
        headers,
        openApi
    );
}

export async function checkIsGiftCardEligible(
    jobNumber: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<boolean> {
    return await RpcClientFactory.getInstance().rpcCall<boolean>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'checkIsGiftCardEligible',
        [jobNumber, sessionId],
        headers,
        openApi
    );
}

export async function createActivity(
    jobId: string,
    sessionId: string,
    onlineActivity: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.activity.OnlineActivityDTO,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'createActivity',
        [jobId, sessionId, onlineActivity],
        headers,
        openApi
    );
}

export async function startONSAFlow(
    jobNumber: string,
    coverableListDTO: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.ProductSelectorDTO,
    preliminaryQuestionsDTO: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.quote.PreliminaryQuestionsDTO,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.AllCoverablesOnsaDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.AllCoverablesOnsaDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'startONSAFlow',
        [jobNumber, coverableListDTO, preliminaryQuestionsDTO, sessionId],
        headers,
        openApi
    );
}

export async function saveCoverableInformation(
    jobNumber: string,
    coverable: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.CoverableInformationToUpdateDTO,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'saveCoverableInformation',
        [jobNumber, coverable, sessionId],
        headers,
        openApi
    );
}

export async function saveClaimsHistoryInformation(
    jobNumber: string,
    coverable: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.ClaimsHistoryInformationToUpdateDTO,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'saveClaimsHistoryInformation',
        [jobNumber, coverable, sessionId],
        headers,
        openApi
    );
}

export async function getAccEmployments(
    jobNumber: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.accident.AccEmploymentDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.accident.AccEmploymentDTO[]
    >(capability.PC_ONLINESALES_UNAUTHENTICATED, 'getAccEmployments', [jobNumber, sessionId], headers, openApi);
}

export async function getPrhContentCovOptions(
    jobNumber: string,
    sessionId: string,
    fixedId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<string[]> {
    return await RpcClientFactory.getInstance().rpcCall<string[]>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getPrhContentCovOptions',
        [jobNumber, sessionId, fixedId],
        headers,
        openApi
    );
}

export async function getPetBreed(
    jobNumber: string,
    flavourName: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.pet.PetBreedDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.pet.PetBreedDTO[]
    >(capability.PC_ONLINESALES_UNAUTHENTICATED, 'getPetBreed', [jobNumber, flavourName, sessionId], headers, openApi);
}

export async function getDogUseList(
    jobNumber: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.pet.DogUseDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.pet.DogUseDTO[]
    >(capability.PC_ONLINESALES_UNAUTHENTICATED, 'getDogUseList', [jobNumber, sessionId], headers, openApi);
}

export async function getPrimaryUserAge(
    jobId: string,
    sessionId: string,
    fixedId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<number> {
    return await RpcClientFactory.getInstance().rpcCall<number>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getPrimaryUserAge',
        [jobId, sessionId, fixedId],
        headers,
        openApi
    );
}

export async function getVehicleTariffGroup(
    jobId: string,
    sessionId: string,
    fixedId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<string> {
    return await RpcClientFactory.getInstance().rpcCall<string>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getVehicleTariffGroup',
        [jobId, sessionId, fixedId],
        headers,
        openApi
    );
}

export async function getHouseBBRData(
    jobId: string,
    sessionId: string,
    fixedId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.house.PrhHouseOnsaDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.house.PrhHouseOnsaDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getHouseBBRData',
        [jobId, sessionId, fixedId],
        headers,
        openApi
    );
}

export async function getHolidayHomeBBRData(
    jobId: string,
    sessionId: string,
    fixedId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.house.PrhHolidayHomeOnsaDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.house.PrhHolidayHomeOnsaDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getHolidayHomeBBRData',
        [jobId, sessionId, fixedId],
        headers,
        openApi
    );
}

export async function getDMRCase(
    jobId: string,
    sessionId: string,
    registrationNumber: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.motor.RegNumberDTO,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.motor.CarOnsaDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.motor.CarOnsaDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getDMRCase',
        [jobId, sessionId, registrationNumber],
        headers,
        openApi
    );
}

export async function setVehicleInheritanceFields(
    jobId: string,
    sessionId: string,
    sourceVehicleId: string,
    targetVehicleId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'setVehicleInheritanceFields',
        [jobId, sessionId, sourceVehicleId, targetVehicleId],
        headers,
        openApi
    );
}

export async function getVMRData(
    jobId: string,
    sessionId: string,
    OnsaMvpVmrPopulator: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.motor.OnsaMvpVmrPopulatorDTO,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.motor.OnsaMvpVmrPopulatorDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.motor.OnsaMvpVmrPopulatorDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getVMRData',
        [jobId, sessionId, OnsaMvpVmrPopulator],
        headers,
        openApi
    );
}

export async function getNumberOfClaimsOptions(
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.activationflow.dto.TypeKeyDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.activationflow.dto.TypeKeyDTO[]
    >(capability.PC_ONLINESALES_UNAUTHENTICATED, 'getNumberOfClaimsOptions', [], headers, openApi);
}

export async function createPackages(
    jobId: string,
    linePrefix: string,
    coverableFixedId: string,
    packageGroupName: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CoveragePackageDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CoveragePackageDTO[]
    >(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'createPackages',
        [jobId, linePrefix, coverableFixedId, packageGroupName, sessionId],
        headers,
        openApi
    );
}

export async function getPackagePrices(
    jobId: string,
    linePrefix: string,
    coverableFixedId: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CoveragePackageDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CoveragePackageDTO[]
    >(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getPackagePrices',
        [jobId, linePrefix, coverableFixedId, sessionId],
        headers,
        openApi
    );
}

export async function saveSelectedPackage(
    jobId: string,
    branchNumber: number,
    linePrefix: string,
    coverableFixedId: string,
    packageGroupName: string,
    packageInfoToUpdate: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.CoveragePackageInformationToUpdateDTO,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'saveSelectedPackage',
        [jobId, branchNumber, linePrefix, coverableFixedId, packageGroupName, packageInfoToUpdate, sessionId],
        headers,
        openApi
    );
}

export async function addOrRemoveCoverage(
    jobId: string,
    linePrefix: string,
    coverableFixedId: string,
    coverageId: string,
    packageGroupName: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'addOrRemoveCoverage',
        [jobId, linePrefix, coverableFixedId, coverageId, packageGroupName, sessionId],
        headers,
        openApi
    );
}

export async function calculateCoveragesPrice(
    jobId: string,
    linePrefix: string,
    coverableFixedId: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.AdditionalCoveragesDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.AdditionalCoveragesDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'calculateCoveragesPrice',
        [jobId, linePrefix, coverableFixedId, sessionId],
        headers,
        openApi
    );
}

export async function updateCoveragePackageInformation(
    jobId: string,
    linePrefix: string,
    coverableFixedId: string,
    packageInfoToUpdate: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.CoveragePackageInformationToUpdateDTO,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'updateCoveragePackageInformation',
        [jobId, linePrefix, coverableFixedId, packageInfoToUpdate, sessionId],
        headers,
        openApi
    );
}

export async function getAdditionalCoverages(
    jobId: string,
    linePrefix: string,
    coverableFixedId: string,
    packageGroupName: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CoverageDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CoverageDTO[]
    >(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getAdditionalCoverages',
        [jobId, linePrefix, coverableFixedId, packageGroupName, sessionId],
        headers,
        openApi
    );
}

export async function getCoverables(
    submissionId: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.quote.AllCoverablesDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.quote.AllCoverablesDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getCoverables',
        [submissionId, sessionId],
        headers,
        openApi
    );
}

export async function getAllOnsaCoverables(
    submissionId: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.AllCoverablesOnsaDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.AllCoverablesOnsaDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getAllOnsaCoverables',
        [submissionId, sessionId],
        headers,
        openApi
    );
}

export async function removeCoverableFromShoppingBasket(
    jobId: string,
    sessionId: string,
    linePrefix: string,
    coverableFixedId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'removeCoverableFromShoppingBasket',
        [jobId, sessionId, linePrefix, coverableFixedId],
        headers,
        openApi
    );
}

export async function updateDummyCustomerDoB(
    jobId: string,
    cprNumber: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'updateDummyCustomerDoB',
        [jobId, cprNumber, sessionId],
        headers,
        openApi
    );
}

export async function getAIDCustomerStop(
    jobId: string,
    cprNumber: string,
    zipCode: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CustomerStopDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CustomerStopDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getAIDCustomerStop',
        [jobId, cprNumber, zipCode, sessionId],
        headers,
        openApi
    );
}

export async function getChanges(
    jobId: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.quote.CoverableChangesDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.onlinesales.dto.quote.CoverableChangesDTO[]
    >(capability.PC_ONLINESALES_UNAUTHENTICATED, 'getChanges', [jobId, sessionId], headers, openApi);
}

export async function getDeductibleOptions(
    jobId: string,
    linePrefix: string,
    coverableFixedId: string,
    coverageId: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.DeductibleOptionDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.onlinesales.dto.DeductibleOptionDTO[]
    >(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getDeductibleOptions',
        [jobId, linePrefix, coverableFixedId, coverageId, sessionId],
        headers,
        openApi
    );
}

export async function getPRCCurrentDeductibleValue(
    jobId: string,
    linePrefix: string,
    coverableFixedId: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<string> {
    return await RpcClientFactory.getInstance().rpcCall<string>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getPRCCurrentDeductibleValue',
        [jobId, linePrefix, coverableFixedId, sessionId],
        headers,
        openApi
    );
}

export async function getYearlyMileageOptions(
    jobId: string,
    coverableFixedId: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.activationflow.dto.TypeKeyDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.activationflow.dto.TypeKeyDTO[]
    >(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getYearlyMileageOptions',
        [jobId, coverableFixedId, sessionId],
        headers,
        openApi
    );
}

export async function getCaravanContentSumOptions(
    jobNumber: string,
    sessionId: string,
    fixedId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CaravanContentSumOptionDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.onlinesales.dto.CaravanContentSumOptionDTO[]
    >(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getCaravanContentSumOptions',
        [jobNumber, sessionId, fixedId],
        headers,
        openApi
    );
}

export async function saveVMRData(
    jobId: string,
    sessionId: string,
    OnsaMvpVmrPopulator: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.onsa.motor.OnsaMvpVmrPopulatorDTO,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'saveVMRData',
        [jobId, sessionId, OnsaMvpVmrPopulator],
        headers,
        openApi
    );
}

export async function getCarIsTrackingRequired(
    jobId: string,
    sessionId: string,
    coverableFixedId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<boolean> {
    return await RpcClientFactory.getInstance().rpcCall<boolean>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getCarIsTrackingRequired',
        [jobId, sessionId, coverableFixedId],
        headers,
        openApi
    );
}

export async function shouldSendClaimsHistory(
    jobId: string,
    linePrefix: string,
    coverableFixedId: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<boolean> {
    return await RpcClientFactory.getInstance().rpcCall<boolean>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'shouldSendClaimsHistory',
        [jobId, linePrefix, coverableFixedId, sessionId],
        headers,
        openApi
    );
}

export async function getPricesAndTaxes(
    jobNumber: string,
    sessionId: string,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.PriceTaxDTO> {
    return await RpcClientFactory.getInstance().rpcCall(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getPricesAndTaxes',
        [jobNumber, sessionId],
        headers,
        openApi
    );
}

export async function getCoverableInfoForOPC(
    jobId: string,
    sessionId: string,
    productName: ProductName_Top,
    coverableId: string,
    isFromDA: boolean,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.CoverableInfoDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.CoverableInfoDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'getCoverableInfoForOPC',
        [jobId, sessionId, productName, coverableId, isFromDA],
        headers,
        openApi
    );
}

export async function calculateAddCoveragePriceForOPC(
    jobId: string,
    sessionId: string,
    addCoverageDTO: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.AddCoverageDTO,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.AddCoveragePriceDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.AddCoveragePriceDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'calculateAddCoveragePriceForOPC',
        [jobId, sessionId, addCoverageDTO],
        headers,
        openApi
    );
}

export async function calculateRemoveCoveragePriceForOPC(
    jobId: string,
    sessionId: string,
    removeCoverageDTO: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.RemoveCoverageDTO,
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.RemoveCoveragePriceDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.opc.RemoveCoveragePriceDTO>(
        capability.PC_ONLINESALES_UNAUTHENTICATED,
        'calculateRemoveCoveragePriceForOPC',
        [jobId, sessionId, removeCoverageDTO],
        headers,
        openApi
    );
}

export async function getOnsaCampaigns(
    openApi = false,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.mittopdanmark.GiftCertificateProductDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<
        DtoTypes.PC.top.edge.capabilities.onlinesales.dto.mittopdanmark.GiftCertificateProductDTO[]
    >(capability.PC_ONLINESALES_UNAUTHENTICATED, 'getOnsaCampaigns', [], headers, openApi);
}
